import { useMutation, useQuery } from "@tanstack/react-query";
import { fetch3, fetch4, fetch5, fetch8 } from "../commonApis/commonApis";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../redux/store";
import {
  addNewOrder,
  setOrdersList,
} from "../features/orderStatusHistories/ordersStatusHistoriesSlice";
const url = process.env.REACT_APP_BASE_URL;

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: any;
}

export const useOrderCreateMutation = () => {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/orders?type=lmClient`,
        payload
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }
      dispatch(addNewOrder(res?.data));
      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Staff Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ staff create request failed:", error.message);
    },
  });
};

export const useFetchOrdersData = (
  page: number,
  searchFor?: string,
  limit?: number,
  filters?: string[]
  // reverse?: boolean,
  // pending?: boolean,
  // cancelled?: boolean,
  // deleted?: boolean,
) => {
  const dispatch: AppDispatch = useDispatch();
  return useQuery({
    queryKey: [
      "ordersData",
      page,
      limit,
      searchFor,
      filters,
      // reverse,
      // pending,
      // cancelled,
      // deleted,
    ], // include dependencies
    queryFn: async () => {
      const filtersParam = encodeURIComponent(JSON.stringify(filters || []));
      console.log("filtersfiltersfilters", filtersParam);

      const res = await fetch3(
        `${url}/api/orders?page=${page}&limit=${limit}&searchFor=${searchFor}&filter=${filtersParam}&type=lmClient`,
        "get"
      );

      if (!res || !res.data) {
        return res.message;
      }
      dispatch(setOrdersList(res.data));
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchOrderDetailsData = (awbNumber?: string) => {
  return useQuery({
    queryKey: ["OrderDetails", awbNumber],
    queryFn: async () => {
      if (!awbNumber) return null; // 👈 early exit inside the hook logic
      const res = await fetch3(
        `${url}/api/orders/${awbNumber}?type=lmClient`,
        "get"
      );
      if (!res || !res.data) return res.message;
      return res.data;
    },
    enabled: !!awbNumber, // 👈 run only when id exists
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchOrderDetailsStatusHistoryData = (awbNumber?: string) => {
  return useQuery({
    queryKey: ["OrderDetailsStatusHistory", awbNumber],
    queryFn: async () => {
      if (!awbNumber) return null; // 👈 early exit inside the hook logic
      const res = await fetch3(
        `${url}/api/orders/${awbNumber}/status-histories?type=lmClient`,
        "get"
      );
      if (!res || !res.data) return res.message;
      return res.data;
    },
    enabled: !!awbNumber, // 👈 run only when id exists
    staleTime: 1000 * 60 * 5,
  });
};

export const useOrderDeleteMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/orders/${id}?type=lmClient`,
        "DELETE",
        payload
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Order deleted successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Order delete request failed:", error.message);
    },
  });
};

export const useFetchDeleteOrdersData = (page: number, searchFor: string) => {
  return useQuery({
    queryKey: ["deleteOrdersData"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/orders/deleted?page=${page}&searchFor=${searchFor}&type=lmClient`,
        "get"
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useOrderUpdateMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/orders/${id}?type=lmClient`,
        "PATCH",
        payload
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Order updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Order update request failed:", error.message);
    },
  });
};

export const useOrderBulkDocMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch8(
        `${url}/api/bulk/bulk-orders`,
        "POST",
        payload
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res;
    },
    onSuccess: (res: any) => {
      console.log("✅ Notification Created successfully:", res);
      return res;
    },
    onError: (error: any) => {
      console.error("❌ Notification create request failed:", error.message);
    },
  });
};

export const useFetchOrderTemplate = () => {
  return useQuery({
    queryKey: ["templates"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/templates/orders`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useOrderSyncMutation = () => {
  return useMutation({
    mutationFn: async () => {
      const res: ApiResponse = await fetch5(
        `${url}/api/orders/sync-order`,
        "PATCH"
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Order deleted successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Order delete request failed:", error.message);
    },
  });
};

export const useOrderLabelPrintMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const response = await fetch(`${url}/api/orders/${id}/generate/pdf`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        mode: "cors",
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch PDF");
      }

      // Get PDF as blob
      const pdfBlob = await response.blob();
      return pdfBlob;
    },
    onSuccess: (pdfBlob: Blob) => {
      console.log("✅ PDF generated successfully");

      // Open PDF in new tab
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");
    },
    onError: (error: any) => {
      console.error("❌ PDF generation failed:", error.message);
      toast.error(error.message || "Failed to generate PDF");
    },
  });
};
